import { useEffect, useState } from 'react';
import { Button, Snackbar, Alert } from '@mui/material';
import { getDocs, collection, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import Header from '../../Header';
import { db } from '../../../firebase';
import OrganizationCard from '../OrganizationCard';
import NewOrganization from '../NewOrganization';
import AlertDialog from '../../components/AlertDialog';
import AdminsModal from '../AdminsModal';

import './styles.css';

const Organizations = () => {
	const navigate = useNavigate();
	const [organizations, setOrganizations] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [showNewOrganization, setShowNewOrganization] = useState(false);
	const [itemToDelete, setItemToDelete] = useState('');
	const [openAlert, setOpenAlert] = useState(false);
	const [disableButtons, setDisableButtons] = useState(false);
	const [projectsExist, setProjectsExist] = useState(false);
	const [showAdmins, setShowAdmins] = useState(false);

	const fetchOrganizations = async () => {
		setLoading(true);
		try {
			const fetched = [];
			const querySnapshot = await getDocs(collection(db, 'organizations'));
			querySnapshot.forEach((doc) => {
				const data = doc.data();
				fetched.push({ id: doc.id, name: data.name });
			});
			setOrganizations(fetched);
		} finally {
			setLoading(false);
		}
	};

	const checkIfOrganizationsContainsDirectory = async (organization) => {
		const directoriesRef = collection(db, 'organizations', organization.id, 'directories');
		const querySnapshot = await getDocs(directoriesRef);
		return !querySnapshot.empty;
	};

	useEffect(() => {
		if (organizations.length === 0) {
			fetchOrganizations();
		}
	}, []);

	const onDeleteOrganizationClick = (organization) => {
		setItemToDelete(organization);
		setOpenAlert(true);
	};

	const deleteDirectory = async (organization) => {
		const directoryRef = doc(db, 'organizations', organization.id);
		try {
			await deleteDoc(directoryRef);
			fetchOrganizations();
			setOpenAlert(false);
			setDisableButtons(false);
		} catch (error) {
			console.error('Error deleting directory: ', error);
		}
	};

	const renameOrganization = async (organization, newName) => {
		if (!newName.trim()) return;
		const organizationRef = doc(db, 'organizations', organization.id);
		try {
			await updateDoc(organizationRef, { name: newName.trim() });
			fetchOrganizations(); 
		} catch (error) {
			console.error('Error renaming organization: ', error);
		}
	};

	const onAlertBoxYesClick = async () => {
		if (await checkIfOrganizationsContainsDirectory(itemToDelete)) {
			setProjectsExist(true);
		} else {
			setDisableButtons(true);
			deleteDirectory(itemToDelete);
		}
	};

	const closeAlert = () => {
		if (!disableButtons) setOpenAlert(false);
	};

	const handleClose = () => setProjectsExist(false);
	const toggleAdmins = () => setShowAdmins((v) => !v);

	const filtered = organizations.filter((org) =>
		org.name.toLowerCase().includes(searchQuery.toLowerCase())
	);

	return (
		<div className="organizations__root">
			<Header />
			<div className="organizations__content">

				{/* Back navigation */}
				<button className="organizations__back-btn" onClick={() => navigate('/')}>
					<ArrowBackIcon />
					Back to home
				</button>

				{/* Page header */}
				<div className="organizations__header">
					<div className="organizations__header-left">
						<h1 className="organizations__title">Organizations</h1>
						<p className="organizations__subtitle">
							Manage organizations, admins, and inspection workspaces.
						</p>
					</div>
					<div className="organizations__header-actions">
						<Button
							variant="outlined"
							onClick={toggleAdmins}
							startIcon={<SupervisorAccountOutlinedIcon />}
							sx={{
								fontFamily: 'Open Sans, sans-serif',
								fontSize: '13.5px',
								fontWeight: 500,
								height: 40,
								borderRadius: '8px',
								borderColor: 'rgba(0,0,0,0.18)',
								color: 'rgba(0,0,0,0.62)',
								textTransform: 'none',
								paddingX: 2,
								'&:hover': {
									borderColor: 'rgba(0,0,0,0.3)',
									background: 'rgba(0,0,0,0.03)',
								},
							}}
						>
							Manage admins
						</Button>
						<Button
							variant="contained"
							onClick={() => setShowNewOrganization(true)}
							startIcon={<AddIcon />}
							sx={{
								fontFamily: 'Open Sans, sans-serif',
								fontSize: '13.5px',
								fontWeight: 600,
								height: 40,
								borderRadius: '8px',
								background: '#0f2d4e',
								textTransform: 'none',
								paddingX: 2.5,
								boxShadow: 'none',
								'&:hover': { background: '#163d68', boxShadow: 'none' },
								'&:active': { background: '#0a1f38', boxShadow: 'none' },
							}}
						>
							Add organization
						</Button>
					</div>
				</div>

				{/* Toolbar */}
				<div className="organizations__toolbar">
					<div className="organizations__search-wrap">
						<SearchIcon className="organizations__search-icon" />
						<input
							className="organizations__search-input"
							placeholder="Search organizations"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</div>
					<span className="organizations__results-label">
						{filtered.length} {filtered.length === 1 ? 'organization' : 'organizations'}
					</span>
				</div>

				{/* Grid */}
				<div className="organizations__grid">
					{showNewOrganization && (
						<NewOrganization
							fetchOrganizations={fetchOrganizations}
							hideNewOrganizationInput={() => setShowNewOrganization(false)}
						/>
					)}
					{loading
						? Array.from({ length: 4 }).map((_, i) => (
							<div key={i} className="organizations__skeleton-card">
								<div className="organizations__skeleton-strip" />
								<div className="organizations__skeleton-body">
									<div className="organizations__skeleton-avatar admin-skeleton" />
									<div className="organizations__skeleton-text">
										<div className="organizations__skeleton-title admin-skeleton" />
										<div className="organizations__skeleton-sub admin-skeleton" />
									</div>
								</div>
							</div>
						))
						: filtered.map((organization) => (
							<OrganizationCard
								key={organization.id}
								organization={organization}
								onDelete={onDeleteOrganizationClick}
								onRename={renameOrganization}
							/>
						))
					}
				</div>
			</div>

			<AlertDialog
				open={openAlert}
				onYesClick={onAlertBoxYesClick}
				handleClose={closeAlert}
				disableButtons={disableButtons}
				itemToDelete={itemToDelete}
			/>
			<AdminsModal toggleModal={toggleAdmins} open={showAdmins} />
			<Snackbar
				open={projectsExist}
				autoHideDuration={6000}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
			>
				<Alert onClose={handleClose} severity="error" variant="filled" sx={{ width: '100%' }}>
					Cannot delete organization — directories exist inside it.
				</Alert>
			</Snackbar>
		</div>
	);
};

export default Organizations;
